import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import api from '../../../../services/api';
import EmpresaSelect from '../../../../components/EmpresaSelect';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { SUPERFICIE, fmtData, getItem } from './estilo';

const ROTA = '/admin/snapshot';

function ehLink(v) {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

/**
 * Entrada do Snapshot Watcher: as janelas de manutencao.
 *
 * Antes esta tela abria num formulario de disparo, com uma lista plana de
 * coletas da empresa inteira embaixo. O operador nao tinha onde "estar" — e a
 * queixa foi literalmente "nao sei se estou na sessao". Agora ela responde
 * primeiro "o que esta acontecendo", e o disparo mora dentro de uma sessao.
 *
 * A coleta avulsa continua existindo, sem cerimonia: "quero conferir este host
 * agora" nao merece abrir janela.
 */
/**
 * Linha da lista de sessoes, no NIVEL DO MODULO.
 *
 * Declarada dentro do componente pai, ela ganhava identidade nova a cada render
 * e o React remontava a linha inteira em vez de atualiza-la — DOM destruido e
 * recriado sob o cursor a cada mudanca de estado da tela.
 */
function Linha({ s, navegar }) {
  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() => navegar(`/admin/hermes/snapshot/sessao/${s.id}`)}
    >
      <TableCell sx={{ width: 60 }}>#{s.id}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{s.name}</Typography>
          {s.ticket && (
            ehLink(s.ticket) ? (
              <Tooltip title="Abrir chamado">
                <IconButton
                  size="small" component="a" href={s.ticket}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpenInNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Chip size="small" label={s.ticket}
                sx={{ height: 17, bgcolor: '#f1f5f9', color: '#475569' }} />
            )
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 110 }} align="right">
        <Typography variant="caption">
          {s.total_coletas} coleta{s.total_coletas === 1 ? '' : 's'}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: 170 }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {s.aberta ? fmtData(s.ultima_coleta || s.created_at) : fmtData(s.closed_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: 44 }} align="right">
        <ChevronRightIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
      </TableCell>
    </TableRow>
  );
}

export default function SnapshotPage() {
  const navegar = useNavigate();
  const notify = useNotify();

  const [empresa, setEmpresa] = useState(getItem(localStorage, 'hermes')?.empresa || null);
  const [sessoes, setSessoes] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const [grupos, setGrupos] = useState([]);
  const [listas, setListas] = useState([]);

  const [dialogo, setDialogo] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [chamado, setChamado] = useState('');
  const [grupoSel, setGrupoSel] = useState(null);
  const [listaSel, setListaSel] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    document.title = 'IPV7 Hermes - Snapshot Watcher';
  }, []);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setCarregando(true);
    try {
      const [s, g, l] = await Promise.all([
        api.get(`${ROTA}/session/list`, { params: { empresa, limit: 50 } }),
        api.get(`${ROTA}/group/tree`, { params: { empresa } }),
        api.post(`${ROTA}/command-set/`, { limit: 200, offset: 0, empresa }),
      ]);
      setSessoes(s.data?.data || []);
      setGrupos(g.data?.data || []);
      setListas(l.data?.data || []);
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao carregar' });
    } finally {
      setCarregando(false);
    }
    // notify vem de um Context estável.
  }, [empresa]);

  useEffect(() => { carregar(); }, [carregar]);

  const gruposFlat = useMemo(() => {
    const out = [];
    const walk = (nos, nivel) => {
      nos.forEach((n) => {
        out.push({ ...n, label: `${' '.repeat(nivel * 4)}${n.name}`, nivel });
        if (n.children?.length) walk(n.children, nivel + 1);
      });
    };
    walk(grupos, 0);
    return out;
  }, [grupos]);

  const abertas = sessoes.filter((s) => s.aberta);
  const encerradas = sessoes.filter((s) => !s.aberta);

  const criar = async () => {
    if (!nome.trim() || !listaSel) return;
    setSalvando(true);
    try {
      const res = await api.post(`${ROTA}/session/create`, {
        empresa,
        name: nome.trim(),
        description: descricao || null,
        ticket: chamado || null,
        group_id: grupoSel?.id || null,
        command_set_id: listaSel,
      });
      if (res.data?.error) {
        notify.setNotify({ open: true, severity: 'error', message: res.data.error });
        return;
      }
      const nova = res.data.data;
      setDialogo(false);
      setNome(''); setDescricao(''); setChamado(''); setGrupoSel(null); setListaSel('');
      // Entra direto na sessão: abrir uma janela e continuar na lista seria um
      // clique a mais para a única coisa que se quer fazer depois de abri-la.
      navegar(`/admin/hermes/snapshot/sessao/${nova.id}`);
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao abrir a sessão' });
    } finally {
      setSalvando(false);
    }
  };


  return (
    <Box sx={{ p: 2, minHeight: '100%', bgcolor: '#f1f5f9' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#0f172a' }}>Snapshot Watcher</Typography>
        <Box sx={{ flex: 1 }} />
        <EmpresaSelect
          sx={{ width: 380, ...SUPERFICIE, borderRadius: 1 }}
          value={empresa}
          handler={(e) => {
            localStorage.setItem('hermes', JSON.stringify({ empresa: e }));
            setEmpresa(e);
          }}
        />
        <Tooltip title="Recarregar">
          <span>
            <IconButton onClick={carregar} disabled={!empresa || carregando}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {!empresa && <Alert severity="info">Selecione uma empresa para começar.</Alert>}

      {empresa && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, ...SUPERFICIE }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>Janelas abertas</Typography>
              {carregando && <CircularProgress size={13} />}
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo(true)}>
                Nova sessão
              </Button>
            </Stack>

            {!abertas.length && !carregando && (
              <Alert severity="success">
                Nenhuma janela aberta. Abra uma antes de mexer na rede — é ela que
                guarda o contexto e agrupa as coletas.
              </Alert>
            )}

            {!!abertas.length && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Janela</TableCell>
                    <TableCell align="right">Coletas</TableCell>
                    <TableCell>Última atividade</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {abertas.map((s) => <Linha key={s.id} s={s} navegar={navegar} />)}
                </TableBody>
              </Table>
            )}
          </Paper>

          {!!encerradas.length && (
            <Paper variant="outlined" sx={{ p: 2, ...SUPERFICIE }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <LockIcon sx={{ fontSize: 17, color: '#64748b' }} />
                <Typography sx={{ fontWeight: 600 }}>Encerradas</Typography>
                <Chip size="small" label={encerradas.length}
                  sx={{ height: 18, bgcolor: '#e2e8f0', color: '#475569' }} />
              </Stack>
              <Table size="small">
                <TableBody>
                  {encerradas.map((s) => <Linha key={s.id} s={s} navegar={navegar} />)}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      <Dialog open={dialogo} onClose={() => setDialogo(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: SUPERFICIE }}>
        <DialogTitle>Nova janela de manutenção</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            O contexto fica aqui e vale para todas as coletas da janela — você não
            vai redigitar o chamado a cada coleta.
          </Alert>

          <TextField
            autoFocus fullWidth size="small" label="Nome da janela" sx={{ mb: 2 }}
            placeholder="Ex.: Troca do uplink do POP-Sul"
            value={nome} onChange={(e) => setNome(e.target.value)}
          />

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={gruposFlat}
              value={grupoSel}
              onChange={(_, v) => setGrupoSel(v)}
              getOptionLabel={(o) => o.label || o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(p) => <TextField {...p} size="small" label="Grupo de hosts" />}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <span style={{ fontFamily: 'monospace' }}>{o.label}</span>
                  <Chip size="small" label={o.host_count} sx={{ ml: 1, height: 18 }} />
                </li>
              )}
            />
            <Select
              size="small" sx={{ flex: 1 }} displayEmpty
              value={listaSel} onChange={(e) => setListaSel(e.target.value)}
            >
              <MenuItem value="" disabled>Lista de comandos</MenuItem>
              {listas.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </Stack>

          <TextField
            fullWidth size="small" label="Chamado" sx={{ mb: 2 }}
            placeholder="Número ou link"
            value={chamado} onChange={(e) => setChamado(e.target.value)}
          />

          <TextField
            fullWidth multiline minRows={3} maxRows={10} label="O que vai ser feito"
            placeholder={'O que vai mudar, e o que se espera que NÃO mude.\n'
              + 'Ex.: troca do uplink. Vizinhos OSPF devem seguir iguais;\n'
              + 'a sessão BGP com a AS antiga sai.'}
            value={descricao} onChange={(e) => setDescricao(e.target.value)}
            helperText="Quem abrir a comparação dias depois lê isto primeiro."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogo(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!nome.trim() || !listaSel || salvando}
            onClick={criar}
          >
            {salvando ? 'Abrindo…' : 'Abrir e entrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
